import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Empty, Input, App, Modal, Tooltip } from "antd";
import {
  DownloadOutlined,
  FileTextOutlined,
  LeftOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  SoundOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/checkCircle.svg";
import { ReactComponent as WarningCircleIcon } from "../../assets/icons/warningCircle.svg";
import { ReactComponent as FailedCircleIcon } from "../../assets/icons/failedCircle.svg";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import theme from "../../themes";
import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch } from "../../store";
import {
  getCampaignByIdThunk,
  getCampaignReportThunk,
} from "../../store/actions/campaign.action";
import { getAgentByIdThunk } from "../../store/actions/agent.action";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { getPresignedAudioUrl, getCampaignReportRecipients } from "../../services/campaign.service";
import { getInboxConversations } from "../../services/whatsapp.service";
import {
  buildCallCsvTable,
  buildConversationCsvTable,
  csvFilename,
  downloadReportCsv,
  fetchAllCallExportRows,
  fetchAllConversationExportRows,
  fetchOutboundRecipientExportRows,
  mapCallExportRows,
  mapConversationExportRows,
} from "./reportExport";
import { parseCallReportPayload } from "./reportPayload";
import ToastMessage from "../../utils/toastMessage";
import ContentLoading, { filterControlCss } from "../../components/ContentLoading";
import {
  kpiBreakCss,
  pageShellCss,
} from "../../styles/responsive";
import { formatSecondsToTime } from "../../utils/formatDecimalNumber";
import {
  generateDynamicColumns,
  MUST_HAVE_CALL_ACTION_KEYS,
  mergeActionKeys,
  collectTakenActionKeys,
  type ReportActionKey,
} from "./utils";
import {
  formatCount,
  formatReportChannel,
  getConversationDisplayStatus,
  inboxApiChannel,
  inboundReportInboxChannel,
  pickReportRecipientCount,
  getCallDurationSeconds,
  getRowChannels,
  isCallAnswered,
  isGoalAchieved,
  isConversationHandledByAi,
  isConversationEscalated,
  isPrimaryCallChannel,
  toPct,
} from "./helpers";
import TablePagination from "./TablePagination";
import { clampPage, resolveTotalPages } from "./pagination";
import { TICKET_DISPLAY_STATUS } from "../../utils/ticketStatus";

dayjs.extend(utc);

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

const readColumnValue = (col: any, row: any) => {
  if (Array.isArray(col.dataIndex)) {
    return col.dataIndex.reduce((acc: any, key: string) => acc?.[key], row);
  }
  if (col.dataIndex) return row[col.dataIndex];
  return row;
};

const renderColumnCell = (col: any, row: any) => {
  const value = readColumnValue(col, row);
  if (typeof col.render === "function") return col.render(value, row);
  if (value === undefined || value === null || value === "") return "-";
  return value;
};

const columnClassName = (key: string) => {
  if (key === "download_audio") return "is-right";
  if (key === "summary") return "is-summary";
  return undefined;
};

const channelIcon = (key: string) => {
  if (key === "call") return <PhoneOutlined />;
  if (key === "sms") return <MessageOutlined />;
  if (key === "whatsapp") return <WhatsAppOutlined />;
  if (key === "email") return <MailOutlined />;
  return key;
};

const conversationStatusColors = (status: string) => {
  if (status === TICKET_DISPLAY_STATUS.COMPLETED) {
    return { text: theme.colors.purple1, background: theme.colors.purple2 };
  }
  if (status === TICKET_DISPLAY_STATUS.FAILED) {
    return { text: "#B42318", background: "#FEE4E2" };
  }
  if (status === TICKET_DISPLAY_STATUS.NEED_ASSIGN) {
    return { text: theme.colors.yellow4, background: theme.colors.yellow3 };
  }
  return { text: theme.colors.green1, background: theme.colors.green2 };
};

const conversationStatusLabel = (
  status: string,
  t: (key: string) => string
) => {
  if (status === TICKET_DISPLAY_STATUS.COMPLETED) {
    return t("inbound.whatsapp.statusCompleted");
  }
  if (status === TICKET_DISPLAY_STATUS.FAILED) {
    return t("inbound.whatsapp.statusFailed");
  }
  if (status === TICKET_DISPLAY_STATUS.NEED_ASSIGN) {
    return t("inbound.whatsapp.needsAssign");
  }
  return t("inbound.whatsapp.statusOpen");
};

const statsFromCallRows = (rows: any[]) => {
  const answeredRows = rows.filter(isCallAnswered);
  return {
    dialled: rows.length,
    answered: answeredRows.length,
    answered_seconds: answeredRows.reduce(
      (sum, row) => sum + getCallDurationSeconds(row),
      0
    ),
    goal_achieved: rows.filter(isGoalAchieved).length,
  };
};

const statsFromConversationRows = (rows: any[]) => {
  const unique = new Set(
    rows
      .map((row) =>
        String(row.phone || row.user_email || row.contact_id || row._id || "")
      )
      .filter(Boolean)
  );
  const closed = rows.filter(isConversationHandledByAi).length;
  const escalated = rows.filter(isConversationEscalated).length;
  return {
    total: rows.length,
    recipients: unique.size,
    closed,
    escalated,
  };
};

type ReportTab = "Inbound" | "Outbound";

const CampaignDetail: React.FC = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [callTimeOrder, setCallTimeOrder] = useState<"ascend" | "descend">(
    "descend"
  );
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const locationState = (location.state as any) || {};
  const [entity, setEntity] = useState<any>(null);
  const [callRows, setCallRows] = useState<any[]>([]);
  const [callKpiStats, setCallKpiStats] = useState<{
    dialled: number;
    answered: number;
    answered_seconds: number;
    goal_achieved: number;
  } | null>(null);
  const [callPagination, setCallPagination] = useState({
    totalItems: 0,
    totalPages: 0,
  });
  const [callActionKeys, setCallActionKeys] = useState<ReportActionKey[]>(
    MUST_HAVE_CALL_ACTION_KEYS
  );
  const [conversationRows, setConversationRows] = useState<any[]>([]);
  const [conversationKpiStats, setConversationKpiStats] = useState<{
    total: number;
    recipients: number;
    closed: number;
    escalated: number;
  } | null>(null);
  const [conversationPagination, setConversationPagination] = useState({
    totalItems: 0,
    totalPages: 0,
  });
  const [tableLoading, setTableLoading] = useState(true);
  const { message } = App.useApp();
  const toast = ToastMessage(message);
  const [summaryDetailRecord, setSummaryDetailRecord] = useState<any | null>(
    null
  );
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const requestIdRef = useRef(0);
  const conversationRequestIdRef = useRef(0);
  const appliedKeywordRef = useRef("");

  const isInboundReport = Boolean(
    locationState.isInbound ||
      locationState.agentName ||
      entity?.direction === "inbound"
  );
  const tab: ReportTab =
    locationState.tab === "Outbound"
      ? "Outbound"
      : locationState.tab === "Inbound"
      ? "Inbound"
      : isInboundReport
      ? "Inbound"
      : "Outbound";
  const displayName =
    locationState.agentName ||
    locationState.campaignName ||
    entity?.agent_name ||
    entity?.campaign_name ||
    "";
  const channels = getRowChannels({
    channels: locationState.channels || entity?.channels,
    channel: locationState.channel || entity?.channel,
  });
  const isCallView = isPrimaryCallChannel({ channels, channel: channels[0] });
  const createdAt = locationState.createdAt || entity?.createdAt;

  const goToReports = (nextTab: ReportTab) => {
    navigate("/reports", {
      state: { tab: nextTab },
    });
  };

  const getSummaryStatusLabel = (rawStatus: any) => {
    if (!rawStatus) return "";
    const key = String(rawStatus);
    const knownKeys = ["achieved", "partially_achieved", "not_achieved"];
    const lookupKey = knownKeys.includes(key) ? key : "unknown";
    const translated = t(`reports.summaryStatus.${lookupKey}`);
    if (
      typeof translated === "string" &&
      translated !== `reports.summaryStatus.${lookupKey}`
    ) {
      return translated;
    }
    return key;
  };

  const mapReportRows = useCallback(
    (rows: any[], inbound: boolean) =>
      rows.map((item: any, index: number) => ({
        ...item,
        key: item.key || item._id || item.id || `row-${index}`,
        phone: inbound ? item.from || item.phone : item.to || item.phone,
        duration:
          item.call_duration !== undefined && item.call_duration !== null
            ? item.call_duration
            : item.duration,
      })),
    []
  );

  const fetchEntity = useCallback(async () => {
    if (!id) return;
    try {
      if (isInboundReport || locationState.agentName) {
        const res = await dispatch(getAgentByIdThunk({ id })).unwrap();
        setEntity(res.data);
        return;
      }
      const res = await dispatch(getCampaignByIdThunk(id)).unwrap();
      setEntity(res.data);
    } catch (error) {
      try {
        const res = await dispatch(getAgentByIdThunk({ id })).unwrap();
        setEntity(res.data);
      } catch (agentError) {
        console.log(agentError);
      }
    }
  }, [dispatch, id, isInboundReport, locationState.agentName]);

  const fetchCampaignReport = useCallback(async () => {
    if (!id || !isCallView) {
      if (!isCallView) {
        setCallRows([]);
        setCallKpiStats(null);
        setCallPagination({ totalItems: 0, totalPages: 0 });
      }
      if (!id) setTableLoading(false);
      return;
    }
    const requestId = ++requestIdRef.current;
    const inbound = Boolean(isInboundReport || locationState.agentName);
    setTableLoading(true);
    try {
      const res = await dispatch(
        getCampaignReportThunk({
          campaignId: inbound ? "" : id,
          agentId: inbound ? id : "",
          keyword: debouncedSearch,
          sort: { start_time: callTimeOrder === "ascend" ? 1 : -1 },
          ...(inbound ? { direction: "inbound" } : {}),
          page: currentPage,
          limit: PAGE_SIZE,
        })
      ).unwrap();
      if (requestId !== requestIdRef.current) return;

      const { rows, pagination, stats, actionKeys } = parseCallReportPayload(
        res.data ?? res
      );
      const mapped = mapReportRows(rows, inbound);
      setCallRows(mapped);
      setCallPagination(pagination);
      setCallActionKeys((prev) =>
        mergeActionKeys(
          MUST_HAVE_CALL_ACTION_KEYS,
          prev,
          actionKeys,
          collectTakenActionKeys(mapped)
        )
      );
      if (stats && typeof stats === "object") {
        setCallKpiStats({
          dialled: Number(stats.dialled) || 0,
          answered: Number(stats.answered) || 0,
          answered_seconds: Number(stats.answered_seconds) || 0,
          goal_achieved: Number(stats.goal_achieved) || 0,
        });
      } else if (!debouncedSearch && mapped.length >= pagination.totalItems) {
        setCallKpiStats(statsFromCallRows(mapped));
      }
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.log(error);
      setCallRows([]);
      setCallPagination({ totalItems: 0, totalPages: 0 });
    } finally {
      if (requestId === requestIdRef.current) setTableLoading(false);
    }
  }, [
    dispatch,
    id,
    isCallView,
    isInboundReport,
    locationState.agentName,
    debouncedSearch,
    callTimeOrder,
    currentPage,
    mapReportRows,
  ]);

  const mapConversationRows = useCallback(
    (messages: any[]) => {
      const inbound = Boolean(isInboundReport || locationState.agentName);
      return mapConversationExportRows(messages, { id, inbound });
    },
    [id, isInboundReport, locationState.agentName]
  );

  const conversationInboxChannel = isInboundReport
    ? inboundReportInboxChannel(channels)
    : inboxApiChannel(channels[0]) || "WHATSAPP_MESSAGE";

  const fetchConversations = useCallback(async () => {
    if (!id || isCallView) {
      setConversationRows([]);
      setConversationKpiStats(null);
      setConversationPagination({ totalItems: 0, totalPages: 0 });
      if (!id) setTableLoading(false);
      return;
    }
    const inbound = Boolean(isInboundReport || locationState.agentName);
    const requestId = ++conversationRequestIdRef.current;
    setTableLoading(true);
    try {
      if (!inbound) {
        const res = await getCampaignReportRecipients(id, {
          page: currentPage,
          limit: PAGE_SIZE,
          keyword: debouncedSearch,
        });
        if (requestId !== conversationRequestIdRef.current) return;
        const { rows, pagination, stats } = parseCallReportPayload(
          res.data ?? res
        );
        const mapped = mapConversationRows(rows);
        setConversationRows(mapped);
        setConversationPagination({
          totalItems: pagination.totalItems || mapped.length,
          totalPages: pagination.totalPages || 1,
        });
        if (stats && typeof stats === "object") {
          setConversationKpiStats({
            total: Number(stats.total) || 0,
            recipients: Number(stats.recipients) || mapped.length,
            closed: Number(stats.closed) || 0,
            escalated: Number(stats.escalated) || 0,
          });
        } else if (!debouncedSearch) {
          setConversationKpiStats(statsFromConversationRows(mapped));
        }
        return;
      }

      const res = await getInboxConversations(conversationInboxChannel, {
        page: currentPage,
        limit: PAGE_SIZE,
        keyword: debouncedSearch,
        include_closed: true,
        agent_id: id,
      });
      if (requestId !== conversationRequestIdRef.current) return;
      const payload = res.data?.data || res.data || res;
      const rows = mapConversationRows(payload?.messages || []);
      const nextPagination = payload?.pagination || {
        totalItems: rows.length,
        totalPages: 1,
      };
      const pagination = {
        totalItems: Number(nextPagination.totalItems) || rows.length,
        totalPages: Number(nextPagination.totalPages) || 1,
      };
      setConversationRows(rows);
      setConversationPagination(pagination);
      if (payload?.stats) {
        setConversationKpiStats({
          total: Number(payload.stats.total) || 0,
          recipients: Number(payload.stats.recipients) || 0,
          closed: Number(payload.stats.closed) || 0,
          escalated: Number(payload.stats.escalated) || 0,
        });
      } else if (!debouncedSearch && pagination.totalItems <= rows.length) {
        setConversationKpiStats(statsFromConversationRows(rows));
      }
    } catch (error) {
      if (requestId !== conversationRequestIdRef.current) return;
      console.log(error);
      setConversationRows([]);
      setConversationKpiStats(null);
      setConversationPagination({ totalItems: 0, totalPages: 0 });
    } finally {
      if (requestId === conversationRequestIdRef.current) setTableLoading(false);
    }
  }, [
    id,
    isInboundReport,
    locationState.agentName,
    isCallView,
    currentPage,
    debouncedSearch,
    conversationInboxChannel,
    mapConversationRows,
  ]);

  const commitSearch = useCallback((raw: string) => {
    const nextKeyword = raw.trim();
    if (appliedKeywordRef.current === nextKeyword) return;
    appliedKeywordRef.current = nextKeyword;
    setDebouncedSearch(nextKeyword);
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    fetchEntity();
  }, [fetchEntity]);

  useEffect(() => {
    fetchCampaignReport();
  }, [fetchCampaignReport]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      commitSearch(search);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search, commitSearch]);

  useEffect(() => {
    appliedKeywordRef.current = "";
    setDebouncedSearch("");
    setSearch("");
    setCurrentPage(1);
    setCallRows([]);
    setCallKpiStats(null);
    setCallActionKeys(MUST_HAVE_CALL_ACTION_KEYS);
    setConversationRows([]);
    setConversationKpiStats(null);
  }, [id]);

  const renderGoalIcon = (rawStatus: any) => {
    const key = String(rawStatus || "unknown");
    if (key === "achieved") return <CheckCircleIcon width={20} height={20} />;
    if (key === "partially_achieved")
      return <WarningCircleIcon width={20} height={20} />;
    if (key === "not_achieved")
      return <FailedCircleIcon width={20} height={20} />;
    return null;
  };

  const handleDownloadAudio = useCallback(
    async (
      storageUrl: string | null,
      sid: string | null,
      recipientName?: string
    ) => {
      if (!storageUrl && !sid) return;
      try {
        const presignedResponse = await getPresignedAudioUrl(
          storageUrl || null,
          sid || null
        );
        const presignedUrl = presignedResponse.data?.data;
        if (!presignedUrl) throw new Error("Failed to get presigned URL");

        let fileExtension = "wav";
        try {
          const urlObj = new URL(presignedUrl);
          const match = urlObj.pathname.match(/\.([^.]+)$/);
          if (match) fileExtension = match[1];
        } catch {
          if (storageUrl) {
            const match = storageUrl.match(/\.([^.]+)(?:\?|$)/);
            if (match) fileExtension = match[1];
          }
        }

        const fileName = recipientName
          ? `call_audio_${recipientName.replace(/\s+/g, "_")}.${fileExtension}`
          : `call_audio_${Date.now()}.${fileExtension}`;
        const link = document.createElement("a");
        link.href = presignedUrl;
        link.setAttribute("download", fileName);
        link.setAttribute("target", "_blank");
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error: any) {
        console.error("Error downloading audio:", error);
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to download audio file",
          5
        );
      }
    },
    [toast]
  );

  const handleDownloadAudioRef = useRef(handleDownloadAudio);
  handleDownloadAudioRef.current = handleDownloadAudio;

  const showCallTable = isCallView || (callKpiStats?.dialled || 0) > 0 || callRows.length > 0;

  const convoKpiSource = conversationKpiStats;

  const inboundConversationCount = debouncedSearch
    ? Number(conversationPagination.totalItems) || 0
    : pickReportRecipientCount(
        convoKpiSource?.total,
        conversationPagination.totalItems,
        locationState.recipients,
        conversationRows.length
      );

  const recipientCount = showCallTable
    ? pickReportRecipientCount(
        callKpiStats?.dialled,
        callPagination.totalItems,
        locationState.recipients,
        entity?.number_of_recipients,
        callRows.length
      )
    : isInboundReport
    ? inboundConversationCount
    : pickReportRecipientCount(
        convoKpiSource?.recipients,
        conversationPagination.totalItems,
        entity?.number_of_recipients,
        locationState.recipients
      );

  const createdLabel = createdAt
    ? dayjs(createdAt).format("h:mm A, MMM D, YYYY")
    : "—";

  const detailMeta = showCallTable
    ? t(
        isInboundReport
          ? "reports.v2.callAgentMeta"
          : "reports.v2.callCampaignMeta",
        {
          recipients: formatCount(recipientCount),
          created: createdLabel,
        }
      )
    : isInboundReport
    ? t("reports.v2.messageAgentMeta", {
        recipients: formatCount(recipientCount),
        created: createdLabel,
      })
    : t("reports.v2.messageCampaignMeta", {
        recipients: formatCount(recipientCount),
        created: createdLabel,
      });

  const kpis = useMemo(() => {
    if (showCallTable) {
      const dialled = callKpiStats?.dialled ?? callRows.length;
      const answered = callKpiStats?.answered ?? callRows.filter(isCallAnswered).length;
      const answeredPct = dialled ? toPct(answered / dialled) : 0;
      const answeredSeconds =
        callKpiStats?.answered_seconds ??
        callRows
          .filter(isCallAnswered)
          .reduce((sum, row) => sum + getCallDurationSeconds(row), 0);
      const avgDuration = answered
        ? formatSecondsToTime(answeredSeconds / answered)
        : "0:00";
      const achieved =
        callKpiStats?.goal_achieved ?? callRows.filter(isGoalAchieved).length;
      const goalPct = answered ? toPct(achieved / answered) : toPct(0);
      return [
        {
          label: t("reports.v2.dialled"),
          value: formatCount(dialled || recipientCount),
          note: t("reports.v2.oneAttemptPerRecipient"),
          color: "#000",
        },
        {
          label: t("reports.v2.answered"),
          value: `${answeredPct}%`,
          note: t("reports.v2.answeredOfCalls", {
            answered: formatCount(answered),
            total: formatCount(dialled),
          }),
          color: theme.colors.green1,
        },
        {
          label: t("reports.v2.averageDuration"),
          value: avgDuration,
          note: t("reports.v2.answeredCallsOnly"),
          color: "#000",
        },
        {
          label: t("reports.v2.goalCompleted"),
          value: `${goalPct}%`,
          note: t("reports.v2.goalCompletedNote"),
          color: "#000",
        },
      ];
    }

    const total = recipientCount;
    const unique = pickReportRecipientCount(
      convoKpiSource?.recipients,
      isInboundReport ? undefined : recipientCount
    );
    const closed =
      convoKpiSource?.closed ??
      conversationRows.filter(isConversationHandledByAi).length;
    const escalated =
      convoKpiSource?.escalated ??
      conversationRows.filter(isConversationEscalated).length;
    return [
      {
        label: t("reports.v2.totalConversations"),
        value: formatCount(total),
        note: t("reports.v2.acrossChannels"),
        color: "#000",
      },
      {
        label: t("reports.v2.totalRecipients"),
        value: formatCount(
          isInboundReport ? unique : unique || recipientCount
        ),
        note: t("reports.v2.uniquePeopleReached"),
        color: "#000",
      },
      {
        label: t("reports.v2.completionRate"),
        value: `${total ? toPct(closed / total) : 0}%`,
        note: t("reports.v2.closedOfConversations", {
          closed: formatCount(closed),
          total: formatCount(total),
        }),
        color: theme.colors.green1,
      },
      {
        label: t("reports.v2.escalated"),
        value: `${total ? toPct(escalated / total) : 0}%`,
        note: t("reports.v2.handedToPerson", { amount: formatCount(escalated) }),
        color: "#000",
      },
    ];
  }, [
    showCallTable,
    callKpiStats,
    callRows,
    conversationKpiStats,
    conversationRows,
    conversationPagination,
    recipientCount,
    isInboundReport,
    t,
  ]);

  const visibleRows = showCallTable ? callRows : conversationRows;
  const serverPagination = showCallTable ? callPagination : conversationPagination;
  const totalItems = Math.max(0, Number(serverPagination.totalItems) || 0);
  const totalPages = resolveTotalPages(
    serverPagination.totalPages,
    totalItems,
    PAGE_SIZE
  );
  const safePage = clampPage(currentPage, totalPages);
  const pageRows = visibleRows;
  const showPager = totalItems > 0 || pageRows.length > 0;

  useEffect(() => {
    if (currentPage !== safePage) {
      setCurrentPage(safePage);
    }
  }, [currentPage, safePage]);

  const callColumns = useMemo(() => {
    const generated = generateDynamicColumns(
      callRows,
      t,
      (record: any) => setSummaryDetailRecord(record),
      {
        order: callTimeOrder,
        onToggle: () => {
          setCallTimeOrder((prev) =>
            prev === "ascend" ? "descend" : "ascend"
          );
          setCurrentPage(1);
        },
      },
      callActionKeys
    ) as any[];

    const extra: any[] = [
      {
        key: "download_audio",
        title: "",
        render: (_: any, row: any) => {
          const hasFile = Boolean(row.storage_url || row.sid);
          return (
            <AudioButton
              type="button"
              aria-label={t("reports.download") || "Download"}
              onClick={() =>
                hasFile
                  ? handleDownloadAudioRef.current(
                      row.storage_url || null,
                      row.sid || null,
                      row.recipient_name
                    )
                  : undefined
              }
              disabled={!hasFile}
            >
              <SoundOutlined />
            </AudioButton>
          );
        },
      },
    ];
    return [...generated, ...extra];
  }, [callRows, t, callTimeOrder, callActionKeys]);

  const handleDownloadCSV = async () => {
    if (!id || downloadingCsv) return;
    const inbound = Boolean(isInboundReport || locationState.agentName);
    setDownloadingCsv(true);
    try {
      let exportRows: any[] = [];
      if (showCallTable) {
        const rows = mapCallExportRows(
          await fetchAllCallExportRows({
            campaignId: inbound ? "" : id,
            agentId: inbound ? id : "",
            sort: { start_time: callTimeOrder === "ascend" ? 1 : -1 },
            ...(inbound ? { direction: "inbound" } : {}),
          }),
          inbound
        );
        downloadReportCsv(
          buildCallCsvTable(rows, t),
          csvFilename(displayName)
        );
        return;
      }

      if (inbound) {
        exportRows = await fetchAllConversationExportRows({
          inbound: true,
          id,
          row: { channels, channel: channels[0] },
        });
      } else {
        try {
          exportRows = await fetchOutboundRecipientExportRows(id);
        } catch (error) {
          console.error("Failed to load campaign recipients", error);
        }
        if (!exportRows.length) {
          exportRows = await fetchAllConversationExportRows({
            inbound: false,
            id,
            row: { channels, channel: channels[0] },
          });
        }
      }
      downloadReportCsv(
        buildConversationCsvTable(exportRows, t),
        csvFilename(displayName)
      );
    } catch (error) {
      console.error("Failed to download CSV", error);
    } finally {
      setDownloadingCsv(false);
    }
  };

  return (
    <Page>
      <ContentLoading spinning={tableLoading}>
        <SectionStack>
        <BackLink onClick={() => goToReports(tab)}>
          <LeftOutlined /> {t("reports.title")}
        </BackLink>

        <HeaderRow>
          <TitleBlock>
            <DetailTitle>{displayName || "—"}</DetailTitle>
            {detailMeta ? <Meta>{detailMeta}</Meta> : null}
          </TitleBlock>
          <CsvButton
            type="button"
            onClick={handleDownloadCSV}
            disabled={downloadingCsv}
          >
            <DownloadOutlined /> {t("reports.downloadCsv")}
          </CsvButton>
        </HeaderRow>

        <KpiGrid>
          {kpis.map((card) => (
            <KpiCard key={card.label}>
              <KpiLabel>{card.label}</KpiLabel>
              <KpiValue $color={card.color}>{card.value}</KpiValue>
              <KpiNote>{card.note}</KpiNote>
            </KpiCard>
          ))}
        </KpiGrid>

        <Panel $flush>
          <Toolbar>
            <SearchWrapper>
              <SearchIcon />
              <CustomSearch
                placeholder={t("reports.searchRecipient")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onPressEnter={() => commitSearch(search)}
                allowClear
              />
            </SearchWrapper>
          </Toolbar>
          <TableScroll>
            <DataTable>
              <thead>
                <tr>
                  {showCallTable ? (
                    callColumns.map((col) => (
                      <th
                        key={col.key}
                        className={columnClassName(col.key)}
                      >
                        {col.title}
                      </th>
                    ))
                  ) : (
                    <>
                      <th>{t("reports.tableColumns.recipientName")}</th>
                      <th>{t("reports.tableColumns.phoneNumber")}</th>
                      <th>{t("reports.v2.channel")}</th>
                      <th>{t("reports.tableColumns.status")}</th>
                      <th>{t("reports.createdTime")}</th>
                      <th>{t("reports.tableColumns.summary")}</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={
                        showCallTable ? callColumns.length : 6
                      }
                    >
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={t("common.noData")}
                      />
                    </td>
                  </tr>
                ) : showCallTable ? (
                  pageRows.map((row) => {
                    return (
                      <tr key={row.key}>
                        {callColumns.map((col) => (
                          <td
                            key={col.key}
                            className={columnClassName(col.key)}
                          >
                            {renderColumnCell(col, row)}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                ) : (
                  pageRows.map((row) => {
                    const channelLabel = formatReportChannel(row, channels[0]);
                    const channelKeys =
                      channelLabel === "—"
                        ? []
                        : channelLabel.split(" · ").filter(Boolean);
                    const when =
                      row.updatedAt ||
                      row.last_message_at ||
                      row.createdAt ||
                      row.start_time;
                    const statusKey = getConversationDisplayStatus(row);
                    return (
                      <tr key={row.key}>
                        <td>
                          <NameLink type="button">
                            {row.recipient_name || row.user_name || "—"}
                          </NameLink>
                        </td>
                        <td>{row.phone || "—"}</td>
                        <td>
                          {channelKeys.length ? (
                            <ChannelCell>
                              {channelKeys.map((key) => (
                                <span key={key}>{channelIcon(key)}</span>
                              ))}
                            </ChannelCell>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          <StatusPill $colors={conversationStatusColors(statusKey)}>
                            {conversationStatusLabel(statusKey, t)}
                          </StatusPill>
                        </td>
                        <td>
                          <RowTime>
                            {when
                              ? dayjs(when).format("h:mm A, MMM D, YYYY")
                              : "—"}
                          </RowTime>
                        </td>
                        <td>
                          <SummaryCell title={row.summary}>
                            {row.summary || "—"}
                          </SummaryCell>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </DataTable>
          </TableScroll>
          {showPager && (
            <TablePagination
              currentPage={safePage}
              totalPages={totalPages}
              totalItems={totalItems || pageRows.length}
              pageSize={PAGE_SIZE}
              onChange={setCurrentPage}
            />
          )}
        </Panel>
        </SectionStack>
      </ContentLoading>

      <StyledSummaryModal
        open={!!summaryDetailRecord}
        onCancel={() => setSummaryDetailRecord(null)}
        footer={null}
        title={
          <ModalTitleRow>
            <ModalTitleIcon>
              <FileTextOutlined />
            </ModalTitleIcon>
            <ModalTitleText>
              {t("reports.summaryDetailTitle") ||
                t("reports.tableColumns.summary") ||
                "Call details"}
            </ModalTitleText>
            {summaryDetailRecord?.recipient_name && (
              <RecipientBadge>{summaryDetailRecord.recipient_name}</RecipientBadge>
            )}
          </ModalTitleRow>
        }
        width="min(660px, calc(100vw - 24px))"
      >
        {summaryDetailRecord && (
          <SummaryModalBody>
            {(() => {
              const ca = summaryDetailRecord.call_analysis || {};
              const followUp = ca.follow_up ?? summaryDetailRecord.follow_up;
              const rawStatus = ca.goal_achieved;
              const status = getSummaryStatusLabel(rawStatus);
              const goalIcon = renderGoalIcon(rawStatus);
              const goalText = followUp?.goal ?? ca.goal ?? status;
              const summary =
                ca.summary ??
                ca.root_cause_analysis?.summary ??
                summaryDetailRecord.summary;
              const reason = ca.reason ?? followUp?.reason;
              const recommendation =
                typeof followUp === "string"
                  ? followUp
                  : followUp?.recommendation;
              const isUnsubscribed =
                String(ca.unsubscribe || "").trim().toUpperCase() === "YES";

              return (
                <>
                  {goalText && (
                    <SummarySection>
                      <SectionLabel>
                        {t("reports.summaryStatus.agentGoal") || "Agent goal"}
                      </SectionLabel>
                      <AgentGoalRow>
                        <span>{goalText}</span>
                        {goalIcon && (
                          <Tooltip title={status || undefined}>
                            <IconWrap aria-label={status || undefined}>
                              {goalIcon}
                            </IconWrap>
                          </Tooltip>
                        )}
                      </AgentGoalRow>
                    </SummarySection>
                  )}
                  {summary && (
                    <SummarySection>
                      <SectionLabel>
                        {t("reports.tableColumns.summary") || "Summary"}
                      </SectionLabel>
                      <SectionContent>{summary}</SectionContent>
                    </SummarySection>
                  )}
                  {reason && (
                    <SummarySection>
                      <SectionLabel>{t("reports.reason") || "Reason"}</SectionLabel>
                      <SectionContent>{reason}</SectionContent>
                    </SummarySection>
                  )}
                  {recommendation && (
                    <SummarySection>
                      <SectionLabel>
                        {t("reports.followUp") || "Follow up"}
                      </SectionLabel>
                      <FollowUpContent>{recommendation}</FollowUpContent>
                    </SummarySection>
                  )}
                  {isUnsubscribed && (
                    <SummarySection>
                      <SectionLabel>Unsubscribe</SectionLabel>
                      <SectionContent>YES</SectionContent>
                    </SummarySection>
                  )}
                </>
              );
            })()}
          </SummaryModalBody>
        )}
      </StyledSummaryModal>
    </Page>
  );
};

export default CampaignDetail;

const Page = styled.div`
  background: ${theme.colors.grey3};
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 32px;
  ${pageShellCss}
`;

const CsvButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-sizing: border-box;
  background: ${theme.colors.green1};
  color: #fff;
  border: none;
  border-radius: 8px;
  height: 42px;
  min-height: 42px;
  padding: 0 16px;
  font-size: 16px;
  line-height: 1;
  font-weight: 400;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
  transition: background 0.2s;
  ${filterControlCss}

  &:hover:not(:disabled) {
    background: #047857;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const SectionStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  min-width: 0;
`;

const BackLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: ${theme.colors.blue1};
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  width: 100%;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

const DetailTitle = styled.h1`
  margin: 0;
  font-weight: 600;
  font-size: 24px;
  line-height: 1;
  color: #000;
`;

const Meta = styled.span`
  font-size: 14px;
  line-height: 1.4;
  color: ${theme.colors.grey1};
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  width: 100%;
  min-width: 0;
  @media (max-width: 1100px) {
    grid-template-columns: 1fr 1fr;
  }
  ${kpiBreakCss}
`;

const KpiCard = styled.div`
  background: #fff;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 12px;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const KpiLabel = styled.span`
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: ${theme.colors.grey1};
`;

const KpiValue = styled.span<{ $color?: string }>`
  font-weight: 700;
  font-size: 32px;
  line-height: 1;
  color: ${({ $color }) => $color || "#000"};

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const KpiNote = styled.span`
  font-size: 14px;
  line-height: 1.4;
  color: ${theme.colors.grey1};
`;

const Panel = styled.div<{ $flush?: boolean }>`
  background: #fff;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 12px;
  padding: ${({ $flush }) => ($flush ? "8px 8px 0" : "24px")};
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 0;
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  flex-wrap: wrap;
  padding: 12px 12px 4px;
`;

const SearchWrapper = styled.div`
  width: 280px;
  max-width: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  padding: 0 14px;
  background: #fff;

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const CustomSearch = styled(Input)`
  height: 40px;
  border: none;
  box-shadow: none;
  background: transparent;
  padding: 0;
  &:hover,
  &:focus {
    border: none;
    box-shadow: none;
  }
`;

const TableScroll = styled.div`
  width: 100%;
  min-width: 0;
  overflow-x: auto;
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 4px;
  text-align: left;

  th,
  td {
    vertical-align: middle;
  }

  th.is-right,
  td.is-right {
    text-align: right;
  }

  th {
    padding: 10px 16px;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: ${theme.colors.grey1};
    background: transparent;
    border: none;
  }

  td {
    padding: 16px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    color: #000;
    background: ${theme.colors.grey4};
    border: none;
    height: 64px;
  }

  tbody tr:nth-child(odd) td {
    background: ${theme.colors.grey3};
  }

  td:first-child {
    border-top-left-radius: 16px;
    border-bottom-left-radius: 16px;
  }

  td:last-child {
    border-top-right-radius: 16px;
    border-bottom-right-radius: 16px;
  }

  th.is-summary,
  td.is-summary {
    min-width: 180px;
    max-width: 280px;
  }
`;

const RowTime = styled.span`
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: #000;
  white-space: nowrap;
`;

const StatusPill = styled.span<{
  $colors: { text: string; background: string };
}>`
  display: inline-flex;
  align-items: center;
  background: ${({ $colors }) => $colors.background};
  color: ${({ $colors }) => $colors.text};
  border-radius: 12px;
  padding: 5px 12px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  white-space: nowrap;
`;

const NameLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: #1890ff;
  cursor: default;
  text-align: left;
`;

const ChannelCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  color: #1f1f1f;
`;

const SummaryCell = styled.div`
  max-width: 280px;
  font-weight: 400;
  color: #111827;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const AudioButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #1f1f1f;
  font-size: 16px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.04);
  }

  &:disabled {
    color: ${theme.colors.grey1};
    cursor: default;
  }
`;

const StyledSummaryModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
  .ant-modal-header {
    padding: 20px 24px;
    border-bottom: 1px solid ${theme.colors.grey2};
  }
  .ant-modal-title {
    font-size: 18px;
    font-weight: 600;
    color: #111827;
  }
  .ant-modal-body {
    padding: 0;
    background: #fff;
  }
  .ant-modal-close {
    top: 20px;
    inset-inline-end: 24px;
    color: ${theme.colors.grey1};
  }
`;

const ModalTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const ModalTitleIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${theme.colors.blue2};
  color: ${theme.colors.blue1};
  font-size: 18px;
  flex-shrink: 0;
`;

const ModalTitleText = styled.span`
  flex: 1;
  min-width: 0;
`;

const RecipientBadge = styled.span`
  padding: 4px 12px;
  border-radius: 16px;
  background: ${theme.colors.grey3};
  color: #4b5563;
  font-size: 13px;
  font-weight: 500;
`;

const SummaryModalBody = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SummarySection = styled.div``;

const SectionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.grey1};
  margin-bottom: 6px;
`;

const SectionContent = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #374151;
`;

const FollowUpContent = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #111827;
  font-weight: 500;
`;

const AgentGoalRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #111827;
`;

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 0;
  svg {
    display: block;
  }
`;
